import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import throttle from 'lodash/throttle'
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'
import templateHeightFactorSelector from 'builder/components/Helper/utils/templateHeightFactorSelector'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { PreviewRefProps } from '../components/DocumentPreview/types'

const PREVIEW_WIDTH = 400
const PREVIEW_WIDTH_MOBILE = 335
const SCREEN_RESIZE_THROTTLE_DELAY = 150

export const useDocumentPreview = () => {
  const previewRef = useRef<PreviewRefProps>(null)
  const previewBoxRef = useRef<HTMLDivElement | null>(null)
  const contentRef = useRef<HTMLDivElement | null>(null)
  const resume = useTypedSelector(state => state.resumeEditor.resume)
  const [currentPageNumber, setCurrentPageNumber] = useState<number>(1)
  const [totalPageNumber, setTotalPageNumber] = useState<number>(0)
  const hasPreviousPage = useMemo(() => currentPageNumber !== 1, [currentPageNumber])
  const { isPhone } = useMediaQueries()
  const [dimensions, setDimensions] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  })
  const pageFormatFactor = templateHeightFactorSelector(resume)

  const hasNextPage = useMemo(
    () => currentPageNumber < totalPageNumber,
    [currentPageNumber, totalPageNumber],
  )

  const handlePreviousPageClick = useCallback(() => {
    if (hasPreviousPage) {
      previewRef.current?.previousPage()
      setCurrentPageNumber(currentPageNumber - 1)
    }
  }, [currentPageNumber, hasPreviousPage, previewRef])

  const handleNextPageClick = useCallback(() => {
    if (hasNextPage) {
      previewRef.current?.nextPage()
      setCurrentPageNumber(currentPageNumber + 1)
    }
  }, [currentPageNumber, hasNextPage, previewRef])

  const handlePaginationStateChange = useCallback(
    (state: { currentPage: number; totalPagesCount: number }) => {
      setCurrentPageNumber(state.currentPage + 1)
      setTotalPageNumber(state.totalPagesCount)
    },
    [],
  )

  const handlePreviewResize = useCallback(() => {
    if (!previewBoxRef.current) return

    const previewWidth = previewBoxRef.current?.offsetWidth
    const width = Math.max(previewWidth, isPhone ? PREVIEW_WIDTH_MOBILE : PREVIEW_WIDTH)
    const height = width / pageFormatFactor
    setDimensions({ width, height })

    if (previewRef.current) {
      previewRef.current?.resize(width)
    }
  }, [isPhone, previewBoxRef, previewRef, pageFormatFactor])

  useEffect(() => {
    const contentElement = contentRef.current
    const handleResizeThrottled = throttle(handlePreviewResize, SCREEN_RESIZE_THROTTLE_DELAY)

    window.addEventListener('resize', handleResizeThrottled)
    handleResizeThrottled()

    if (contentElement) disableBodyScroll(contentElement)
    return () => {
      if (contentElement) enableBodyScroll(contentElement)
      window.removeEventListener('resize', handleResizeThrottled)
    }
  }, [contentRef, handlePreviewResize])

  return {
    handlePreviousPageClick,
    handleNextPageClick,
    handlePaginationStateChange,
    hasPreviousPage,
    hasNextPage,
    totalPageNumber,
    currentPageNumber,
    resume,
    dimensions,
    previewRef,
    previewBoxRef,
    contentRef,
  }
}
